import { useEffect, useMemo } from "react";
import dayjs from "dayjs";

const normalizeStr = (str) =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

export default function useExpedientesProcedimiento({
  concSlice,
  search,
  statusFilter,
  fueroFilter,
  empresaId,
  dateRange,
  currentPage,
  pageSize,
  setCurrentPage,
}) {
  const expedientesItems = useMemo(() => {
    const raw = concSlice?.data;
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.items)) return raw.items;
    return [];
  }, [concSlice]);

  const searchedExpedientes = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return expedientesItems;

    return expedientesItems.filter((exp) => {
      const campos = [
        exp.numero_expediente,
        exp.folio,
        exp.id,
        exp.parte_actora,
        exp.parte_demandada,
        exp.nombre_solicitante,
        exp.solicitante,
        exp.empresa_nombre,
        exp.nombre_empresa,
        exp.razon_social,
        exp.trabajador,
        exp.nombre_trabajador,
      ];
      return campos.some((v) => v && String(v).toLowerCase().includes(term));
    });
  }, [search, expedientesItems]);

  // Clasifica un expediente en uno de los 4 buckets usando los IDs de BD primero,
  // con fallback a string para registros legacy.
  const getExpBucket = (exp) => {
    const idEstado = exp?.id_desvinculacion_estado ? Number(exp.id_desvinculacion_estado) : null;
    const idResultado = exp?.id_desvinculacion_resultado ? Number(exp.id_desvinculacion_resultado) : null;

    if (idResultado === 1) return "solucionadas";
    if (idResultado === 2) return "esperaCitatorio";
    if (idEstado === 2) return "enProceso";
    if (idEstado === 1 || idEstado === 3) return "registradas"; // cancelada también cae en registradas para no perder registros

    // Fallback string
    const raw = normalizeStr(exp?.status_nombre || exp?.status || exp?.estatus || "");
    if (raw.includes("solucion") || raw.includes("exito")) return "solucionadas";
    if (raw.includes("citatorio") || raw.includes("prejudicial") || raw.includes("espera")) return "esperaCitatorio";
    if (raw.includes("proceso")) return "enProceso";
    return "registradas";
  };

  // Vencido: la fecha de baja ya pasó y el expediente sigue abierto (registrada / en proceso).
  const isVencido = (exp) => {
    const fecha = exp?.fecha_baja || exp?.fecha_ejecucion || exp?.fecha_solicitacion;
    if (!fecha) return false;
    const d = dayjs(fecha);
    if (!d.isValid() || !d.isBefore(dayjs())) return false;
    const bucket = getExpBucket(exp);
    return bucket === "registradas" || bucket === "enProceso";
  };

  const statusCounts = useMemo(() => {
    const base = { registradas: 0, enProceso: 0, solucionadas: 0, esperaCitatorio: 0, vencidos: 0 };
    searchedExpedientes.forEach((exp) => {
      const bucket = getExpBucket(exp);
      base[bucket] += 1;
      if (isVencido(exp)) base.vencidos += 1;
    });
    return base;
  }, [searchedExpedientes]);

  const filteredExpedientes = useMemo(() => {
    let list = [...searchedExpedientes];

    if (statusFilter === "vencidos") {
      list = list.filter((exp) => isVencido(exp));
    } else if (statusFilter !== "todos") {
      list = list.filter((exp) => getExpBucket(exp) === statusFilter);
    }

    if (empresaId) {
      list = list.filter((exp) => {
        const idEmp =
          exp.id_empresa ??
          exp.empresa_id ??
          exp.empresa?.id ??
          exp.id_empresa_demandada;
        return String(idEmp) === String(empresaId);
      });
    }

    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      list = list.filter((exp) => {
        const fechaRaw =
          exp.fecha_baja ||
          exp.fecha_ejecucion ||
          exp.fecha_encargo ||
          exp.fecha ||
          exp.fecha_solicitud ||
          exp.created_at;
        if (!fechaRaw) return false;
        const d = dayjs(fechaRaw);
        return (
          d.isValid() &&
          (d.isSame(start, "day") || d.isAfter(start, "day")) &&
          (d.isSame(end, "day") || d.isBefore(end, "day"))
        );
      });
    }

    return list;
  }, [searchedExpedientes, statusFilter, empresaId, dateRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, empresaId, dateRange]);

  const paginatedExpedientes = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredExpedientes.slice(start, start + pageSize);
  }, [filteredExpedientes, currentPage, pageSize]);

  return {
    statusCounts,
    filteredExpedientes,
    paginatedExpedientes,
    totalExp: filteredExpedientes.length,
  };
}
